import { useCallback, useEffect, useState } from 'react';
import { type Address, recoverMessageAddress } from 'viem';
import { useSignMessage as useSignMessageWagmi } from 'wagmi';

interface SignMessageResult {
  signature: string | undefined;
  recoveredAddress: Address | undefined;
  error: Error | null;
  isPending: boolean;
  signMessage: (args: { message: string }) => void;
  reset: () => void;
}

export function useSignMessage(): SignMessageResult {
  const [recoveredAddress, setRecoveredAddress] = useState<Address>();
  const { data: signature, variables, error, isPending, signMessage, reset: resetSignMessage } = useSignMessageWagmi();

  const recoverAddress = useCallback(async () => {
    try {
      if (!variables?.message || !signature) {
        return;
      }

      const recovered = await recoverMessageAddress({
        message: variables.message,
        signature,
      });
      setRecoveredAddress(recovered);
    } catch (err) {
      console.error('Error recovering address:', err);
      setRecoveredAddress(undefined);
    }
  }, [signature, variables?.message]);

  const reset = useCallback(() => {
    setRecoveredAddress(undefined);
    resetSignMessage();
  }, [resetSignMessage]);

  useEffect(() => {
    recoverAddress();
  }, [recoverAddress]);

  return {
    signature,
    recoveredAddress,
    error,
    isPending,
    signMessage,
    reset,
  };
}
